import React, { useState, useMemo } from 'react'
import { cloneDeep } from 'lodash'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
// @ts-ignore
import { Dialog } from '@netbasequid/core'
import InsightDialog from 'components/insightContent/visualization/InsightDialog'
import { InsightData } from 'schemas/action/InsightDataSchema'
import FluctuationSummaryMetrics from 'components/insightContent/Overview/DetailItems/Fluctuation/SummaryMetrics'
import MeasureType from 'enums/MeasureType'
import FluctuationPopularTerms from 'components/insightContent/Overview/DetailItems/Fluctuation/PopularTerms'
import { Theme } from 'features/ui/types'
import { useSysTheme, useDisplayTheme } from 'hooks/useUiState'
import { SystemTheme } from 'hooks/useUiState/types'
import {
  TopInfoBox,
  TopInfoRight,
  StyledTermContainer,
  StyledTermContent
} from 'components/insightContent/visualization/InfoBox/index.css'
import GoToAnalysisButton from 'components/insightContent/visualization/GoToAnalysisButton'
import AnnotationButton from 'components/insightContent/visualization/AnnotationButton'
import Annotation from 'components/insightContent/Annotation'
import { sendTypedMessage } from 'utils/iframeUtil'
import { toExternal } from 'vo/util'
import { formatTimeRange } from './utils'
import { TimelineWidget } from '../../../types'
import useAnnotationData from './hooks/useAnnotationData'

const StyledDialogContent = styled(Dialog.DialogContent)`
  & > div:nth-of-type(2) {
    display: flex;
    flex-direction: column;
    & > div:nth-of-type(2) {
      flex: 1;
      display: flex;
      flex-direction: column;
    }
  }
`

const StyledTopInfoRight = styled(TopInfoRight)`
  display: flex;
  gap: 8px;
`

const TimeRange = styled.span<{ $theme: SystemTheme }>`
  font-weight: ${({ $theme }) => $theme.fontWeight.semibold};
`

const DIALOG_CONTENT_HEIGHT = '462px'

interface InsightModalProps {
  title?: string
  open: boolean
  onClose: () => void
  selectedInsights?: InsightData[]
  widgetSetting: TimelineWidget
  isAudienceQuery?: boolean
}

const InsightModal = ({
  title,
  open,
  onClose,
  selectedInsights,
  widgetSetting,
  isAudienceQuery
}: InsightModalProps) => {
  const mode = useDisplayTheme() as Theme
  const theme = useSysTheme()
  const { t } = useTranslation()
  const {
    annotations,
    updateAnnotation,
    deleteAnnotation,
    addAnnotation,
    timeZone,
    isOperating,
    operatingId,
    operable
  } = useAnnotationData(widgetSetting, selectedInsights)
  const [showAnnotation, setShowAnnotation] = useState(false)

  const summaryMetricsCompactWidgetSetting = useMemo(
    () => ({
      ...cloneDeep(widgetSetting),
      selectedMeasures: [],
      measures: [MeasureType.POSTS, MeasureType.NET_SENTIMENT, MeasureType.IMPRESSIONS],
      showChange: true
    }),
    [widgetSetting]
  )

  function goToAnalysis() {
    sendTypedMessage('goToAnalysis', toExternal(widgetSetting))
  }

  return (
    <InsightDialog open={open} onClose={onClose} width="1152px">
      <Dialog.DialogTitle>{title}</Dialog.DialogTitle>
      <div style={{ display: 'flex' }}>
        <div>
          <TopInfoBox mode={mode}>
            <StyledTermContainer>
              <StyledTermContent mode={mode}>
                {`${t('summaryMetrics')}: `}
                <TimeRange $theme={theme}>
                  {formatTimeRange(
                    {
                      start: selectedInsights?.[0]?.startDatetime,
                      end: selectedInsights?.[0]?.endDatetime
                    },
                    timeZone
                  )}
                </TimeRange>
              </StyledTermContent>
            </StyledTermContainer>
            <StyledTopInfoRight>
              <GoToAnalysisButton onClick={() => goToAnalysis()} />
              <AnnotationButton onClick={() => setShowAnnotation(true)} />
            </StyledTopInfoRight>
          </TopInfoBox>
          <StyledDialogContent
            height={DIALOG_CONTENT_HEIGHT}
            width={showAnnotation ? '855px' : '1152px'}>
            <FluctuationSummaryMetrics
              widgetSetting={summaryMetricsCompactWidgetSetting}
              isAudienceQuery={isAudienceQuery}
            />
            <FluctuationPopularTerms
              widgetSetting={widgetSetting}
              isAudienceQuery={isAudienceQuery}
            />
          </StyledDialogContent>
        </div>
        <div style={{ display: 'flex', justifyContent: 'center', height: '510px' }}>
          {showAnnotation && (
            <Annotation
              annotations={annotations}
              onClose={() => setShowAnnotation(false)}
              onUpdate={updateAnnotation}
              onDelete={deleteAnnotation}
              onAdd={addAnnotation}
              isOperating={isOperating}
              operatingId={operatingId}
              operable={operable}
            />
          )}
        </div>
      </div>
      <Dialog.DialogActions />
    </InsightDialog>
  )
}

export default InsightModal
